import { useLayoutEffect, useRef, useState } from "react";

export default function AutoFitText({
  children,
  min = 8,
  max = 48,
  maxWidth, // NEW: the stable width ceiling (px) available for the text,
            // net of any ancestor padding — pass this from the parent.
  className = "",
  style = {},
}) {
  const containerRef = useRef(null);
  const [fontSize, setFontSize] = useState(min);

  useLayoutEffect(() => {
    if (!containerRef.current) return;
    const container = containerRef.current;

    const measure = document.createElement("div");
    measure.style.position = "absolute";
    measure.style.left = "-9999px";
    measure.style.top = "-9999px";
    measure.style.visibility = "hidden";
    measure.style.pointerEvents = "none";
    measure.style.whiteSpace = "normal";
    measure.style.wordBreak = "normal";
    measure.style.overflowWrap = "normal";
    measure.style.lineHeight = "1.05";
    measure.style.textAlign = "center";
    document.body.appendChild(measure);

    const fit = () => {
      const cs = getComputedStyle(container);
      measure.style.fontFamily = cs.fontFamily;
      measure.style.fontWeight = cs.fontWeight;
      measure.textContent = container.textContent || "";

      // Height is genuinely stable (fixed-height card), fine to read live.
      const availH = container.clientHeight;

      // Width must NOT be read from the live box, since the box's own
      // width changes as a *result* of the font size we're solving for
      // (max-content ancestor + wrapping). Use the fixed design ceiling.
      const paddingX =
        parseFloat(cs.paddingLeft || 0) + parseFloat(cs.paddingRight || 0);
      const widthCeiling =
        (typeof maxWidth === "number" ? maxWidth : container.clientWidth) -
        paddingX;
      const availW = Math.max(widthCeiling, 0);

      if (availW <= 0 || availH <= 0) return;

      measure.style.width = availW + "px";

      let lo = min;
      let hi = max;
      let best = min;
      while (lo <= hi) {
        const mid = Math.floor((lo + hi) / 2);
        measure.style.fontSize = mid + "px";
        const w = Math.max(measure.scrollWidth, measure.offsetWidth);
        const h = measure.offsetHeight;
        if (w <= availW && h <= availH) {
          best = mid;
          lo = mid + 1;
        } else {
          hi = mid - 1;
        }
      }
      setFontSize(best);
    };

    fit();
    // Height can still change (e.g. layout shifts), width won't anymore
    // since it's no longer derived from the box we're mutating.
    const ro = new ResizeObserver(fit);
    ro.observe(container);
    return () => {
      ro.disconnect();
      if (measure.parentNode) measure.parentNode.removeChild(measure);
    };
  }, [children, min, max, maxWidth]);

  return (
    <div
      ref={containerRef}
      className={className}
      style={{
        fontSize: `${fontSize}px`,
        lineHeight: 1.05,
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        wordBreak: "break-word",
        overflowWrap: "anywhere",
        overflow: "hidden",
        whiteSpace: "normal",
        ...style,
      }}
    >
      {children}
    </div>
  );
}